import { Endpoints } from '@/constants';
import { store } from '@/store';

import { getLocationList as getLocationListOIC } from './ManhattanService';
import { getMiddlewareClient, buildTransmissionHeader } from './taskService';

import type {
  UpdateLocationRequest,
  UpdateLocationResponse,
  LocationDetail,
  RequestTraceData,
} from './types/dockDoorTypes';

const DOCK_DOOR_ACTIONS = {
  updateLocation: 'updateLocation',
} as const;

function getTraceData(): RequestTraceData {
  const state = store.getState();
  return {
    User: state.user.user?.first_name ?? 'SYSTEM',
    Plant: state.user.plant?.plant_name ?? '',
    SessionId: state.user.sessionId ?? '',
  };
}

function getOrgId(): string {
  return String(store.getState().user.plant?.org_id ?? '');
}

function getUserId(): string {
  return store.getState().user.user?._id ?? '';
}

function getUserName(): string {
  const user = store.getState().user.user;
  return user?.first_name ?? 'SYSTEM';
}

// Matches Xamarin: all Manhattan middleware calls use MIDDLEWARE_FUNCTION_CODES.update_task
function getFunctionCode(): string {
  return store.getState().global.middlewareFunctionCodes?.update_task ?? '';
}

function getAuthHeaders(): Record<string, string> {
  const state = store.getState();
  const token = state.user.accessToken;
  if (!token) {
    throw new Error('No access token available. Please log in again.');
  }
  return { Authorization: `Bearer ${token}` };
}

/**
 * Fetch dock door locations via OIC direct endpoint.
 * Matches Xamarin DockDoorPageViewModel → MiddlewareService.GetLocationListAsync(AppConstants.DockDoorFilterItem)
 * Endpoint: NBL_YMS_GET_LOCATI_LIST/1.0/getLocationList
 */
export async function getLocationList(locationType: string): Promise<LocationDetail[]> {
  const locations = await getLocationListOIC(locationType);

  // Map MoveLocationDetail → LocationDetail for DockDoorTable compatibility
  // OIC returns zone ("PHI") and name ("09") separately — combine as "PHI - 09"
  // Matches Xamarin display format with spaces around dash
  return locations.map((loc) => ({
    Location: loc.LocationZone ? `${loc.LocationZone} - ${loc.LocationName}` : loc.LocationName,
    LocStatus: loc.LocStatus,
    TrailerName: loc.TrailerName,
    CarrierName: loc.CarrierName,
    CarrierCode: loc.CarrierCode,
    LastAuditTime: loc.LastAuditTime,
  }));
}

export async function updateLocation(
  locationName: string,
  locationStatus: string,
  locationZone: string,
  locationType: string
): Promise<UpdateLocationResponse> {
  const request: UpdateLocationRequest = {
    Payload: {
      TransmissionHeader: buildTransmissionHeader(getUserName()),
      Body: {
        LocationName: locationName,
        OrgId: getOrgId(),
        LocationStatus: locationStatus,
        LocationZone: locationZone,
        UserId: getUserId(),
        LocationType: locationType,
      },
    },
    TraceData: getTraceData(),
  };

  const client = getMiddlewareClient();
  const response = await client.post<UpdateLocationResponse>(
    Endpoints.buildMiddlewareUrl(getFunctionCode(), DOCK_DOOR_ACTIONS.updateLocation),
    request,
    { headers: getAuthHeaders() }
  );

  return response.data;
}

export const dockDoorApi = {
  getLocationList,
  updateLocation,
};
